import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import defaultAxios from "../configurations/DefaultAxiosConfig";
import ErrorModel, { IErrorModel } from "../models/DTO/ErrorModel";
import ApiResponseModel from "../models/API/ApiResponseModel";

export type HandledResponseType<TData = any> = { IsSuccess: boolean, Data?: TData, Messages: string[], Error?: IErrorModel };

const HandleError = <TData>(exception: AxiosError<any, TData>): { Response?: AxiosResponse<any, TData>, Error?: IErrorModel } => {

    const { request, response } = exception,
        model: IErrorModel = new ErrorModel(undefined, (request == null || request.status == 0) ? 500 : request.status, exception),
        result = {
            Error: (model.CustomMessage != "Requisição cancelada." && model) || undefined,
            Response: response,
        };

    return result;
}

const HandlePromise = async<TEntity>(promise: Promise<AxiosResponse<any, any>>, handle: (handledResponse: HandledResponseType<TEntity>) => TEntity): Promise<TEntity | HandledResponseType<TEntity>> => {
    const result = promise
        .then(response => {
            const handled: HandledResponseType<TEntity> = HandleResponse(response, undefined),
                result: TEntity = handle(handled);
            return result;
        })
        .catch((ex: AxiosError<any, TEntity>) => {
            const { Response, Error } = HandleError(ex);
            return HandleResponse<TEntity>(undefined, Error);
        });

    return result;
}

export const HandleResponse = <TData>(response?: AxiosResponse<TData, any>, error?: IErrorModel): HandledResponseType<TData> => {
    const api: ApiResponseModel | undefined = (error == null && response != null && new ApiResponseModel(response)) || undefined,
        data = (api && api.Data) || undefined,
        denyMessages = ["Usuário não autorizado para realizar esta operação", "Usuário sem permissão de login na empresa"],
        messages: string[] = api?.Message || [],
        message403: string | undefined = messages.find(msg => denyMessages.some(x => msg.startsWith(x))),
        err: IErrorModel | undefined = (message403 != null && message403 != "" && new ErrorModel(message403, 403)) || error,
        isSuccess: boolean = error == null && api != null && api.Success,
        result = {
            Data: data,
            IsSuccess: isSuccess,
            Messages: messages,
            Error: err
        };

    return result;
}

export function Get<TData>(endpoint: string, reqConfig: AxiosRequestConfig | undefined, callback: (response?: AxiosResponse<any, TData>, error?: IErrorModel) => any): void {
    if (!endpoint || endpoint == "")
        throw new Error("Endpoint could not be null.");

    defaultAxios
        .get(endpoint, reqConfig)
        .then(response => callback && callback(response))
        .catch((ex: AxiosError<any, TData>) => {
            const { Response, Error } = HandleError(ex);
            callback(Response, Error);
        });
}

export function Post<TData>(endpoint: string, data: TData, reqConfig: AxiosRequestConfig<TData> | undefined, callback: (response?: AxiosResponse<any, TData>, error?: IErrorModel) => any): void {

    if (!endpoint || endpoint == "")
        throw new Error("Endpoint could not be null.");

    defaultAxios
        .post(endpoint, data, reqConfig)
        .then((response: AxiosResponse<any, TData>) => callback && callback(response))
        .catch((ex: AxiosError<any, TData>) => {
            const { Response, Error } = HandleError(ex);
            callback(Response, Error);
        });
}

export function Put<TData>(endpoint: string, data: TData, reqConfig: AxiosRequestConfig<TData> | undefined, callback: (response?: AxiosResponse<any, TData>, error?: IErrorModel) => any): void {
    if (!endpoint || endpoint == "")
        throw new Error("Endpoint could not be null.");

    defaultAxios
        .put(endpoint, data, reqConfig)
        .then((response: AxiosResponse<any, TData>) => callback && callback(response))
        .catch((ex: AxiosError<any, TData>) => {
            const { Response, Error } = HandleError(ex);
            callback(Response, Error);
        });
}

export const Delete = (endpoint: string, reqConfig: AxiosRequestConfig | undefined, callback: (response?: AxiosResponse<any, any>, error?: IErrorModel) => any): void => {
    if (!endpoint || endpoint == "")
        throw new Error("Endpoint could not be null.");

    defaultAxios
        .delete(endpoint, reqConfig)
        .then(response => callback(response))
        .catch((ex: AxiosError<any, any>) => {
            const { Response, Error } = HandleError(ex);
            callback(Response, Error);
        });
}

export function Patch<TData>(endpoint: string, data: TData, reqConfig: AxiosRequestConfig<TData> | undefined, callback: (response?: AxiosResponse<any, TData>, error?: IErrorModel) => any): void {
    if (!endpoint || endpoint == "")
        throw new Error("Endpoint could not be null.");

    defaultAxios
        .patch(endpoint, data, reqConfig)
        .then((response: AxiosResponse<any, TData>) => callback && callback(response))
        .catch((ex: AxiosError<any, TData>) => {
            const { Response, Error } = HandleError(ex);
            callback(Response, Error);
        });
}

export const GetAsync = async <TEntity = any>(endpoint: string, reqConfig: AxiosRequestConfig | undefined, handle: (handledResponse: HandledResponseType<TEntity>) => TEntity): Promise<TEntity | HandledResponseType<TEntity>> => {
    if (!endpoint || endpoint == "")
        throw new Error("Endpoint could not be null.");

    const response = HandlePromise(defaultAxios.get(endpoint, reqConfig), handle);

    return response;
}

export const PostAsync = async <TEntity = any>(endpoint: string, data: TEntity, reqConfig: AxiosRequestConfig<TEntity> | undefined, handle: (handledResponse: HandledResponseType<TEntity>) => TEntity): Promise<TEntity | HandledResponseType<TEntity>> => {
    if (!endpoint || endpoint == "")
        throw new Error("Endpoint could not be null.");

    const response = HandlePromise(defaultAxios.post(endpoint, data, reqConfig), handle);

    return response;
}

export const PutAsync = async<TEntity = any>(endpoint: string, data: TEntity, reqConfig: AxiosRequestConfig<TEntity> | undefined, handle: (handledResponse: HandledResponseType<TEntity>) => TEntity): Promise<TEntity | HandledResponseType<TEntity>> => {

    if (!endpoint || endpoint == "")
        throw new Error("Endpoint could not be null.");

    const response = HandlePromise(defaultAxios.put(endpoint, data, reqConfig), handle);

    return response;
}

export const DeleteAsync = async<TEntity = any>(endpoint: string, data: TEntity, reqConfig: AxiosRequestConfig<TEntity> | undefined, handle: (handledResponse: HandledResponseType<TEntity>) => TEntity): Promise<TEntity | HandledResponseType<TEntity>> => {
    if (!endpoint || endpoint == "")
        throw new Error("Endpoint could not be null.");

    const response = HandlePromise(defaultAxios.delete(endpoint, reqConfig), handle);

    return response;
}

export const PatchAsync = async<TEntity = any>(endpoint: string, data: TEntity, reqConfig: AxiosRequestConfig<TEntity> | undefined, handle: (handledResponse: HandledResponseType<TEntity>) => TEntity): Promise<TEntity | HandledResponseType<TEntity>> => {
    if (!endpoint || endpoint == "")
        throw new Error("Endpoint could not be null.");

    const response = HandlePromise(defaultAxios.patch(endpoint, reqConfig), handle);

    return response;
}

export const All = <TData>(requests: Promise<TData>[], callback: (arrayResponse: TData[]) => void): void => {
    axios
        .all<TData>(requests)
        .then(arrayResponse => callback(arrayResponse));
}

export const GetStream = (endpoint: string, reqConfig: AxiosRequestConfig<any>, callback: (response?: AxiosResponse<any, any>, error?: IErrorModel) => any): void => {
    if (!endpoint || endpoint == "")
        throw new Error("Endpoint could not be null.");

    defaultAxios
        .get(endpoint, reqConfig)
        .then(response => {
            callback && callback(response)
        })
        .catch((ex: AxiosError<any, any>) => {
            const { Response, Error } = HandleError(ex);
            callback(Response, Error);
        });
}

export const PostStream = <TData>(endpoint: string, data: TData, reqConfig: AxiosRequestConfig<any>, callback: (response?: AxiosResponse<any, any>, error?: IErrorModel) => any): void => {
    if (!endpoint || endpoint == "")
        throw new Error("Endpoint could not be null.");

    defaultAxios
        .post(endpoint, data, reqConfig)
        .then(response => {
            callback && callback(response)
        })
        .catch((ex: AxiosError<any, any>) => {
            const { Response, Error } = HandleError(ex);
            callback(Response, Error);
        });
}